'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Radio, Video, Youtube, ChevronUp, ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { track } from '@/lib/analytics';
import { useLang } from '@/lib/i18n/LanguageContext';
import { describeEventTime, fetchNextLiveEvent, type LiveEvent } from '@/lib/liveEvents';
import { liveRoomHref } from '@/lib/liveRooms';

// Next scheduled live session. `variant="banner"` sits at the top of the /live chat pane;
// `variant="card"` is the feed card. Renders nothing when there is no upcoming session.
export default function LiveEventBanner({ variant = 'banner', currentRoom }: { variant?: 'banner' | 'card'; currentRoom?: string | null }) {
  const { t, lang } = useLang();
  const [ev, setEv] = useState<LiveEvent | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchNextLiveEvent().then((e) => { if (alive) setEv(e); });
    const id = setInterval(() => setTick((x) => x + 1), 30_000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  void tick;

  // Lecture on Google Meet (owner, 2026-09-23): the link comes from /api/live-events/[id]/join and
  // is only returned to signed-in users; guests see "sign up for the link" instead.
  const { session, loading: authLoading } = useAuth();
  // Since 2026-09-30 the lecture can also be a YouTube live stream (unlisted), played right here
  // above the chat for signed-in users; Meet (if set) stays as a secondary button.
  const [meet, setMeet] = useState<{ hasMeet: boolean; url: string | null; hasVideo: boolean; youtubeId: string | null } | null>(null);
  const [videoOpen, setVideoOpen] = useState(true);
  const tracked = useRef<string | null>(null);
  useEffect(() => {
    if (!ev || authLoading) return;
    let alive = true;
    const headers: Record<string, string> = {};
    if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
    fetch(`/api/live-events/${ev.id}/join`, { headers, credentials: 'include', cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => { if (alive) setMeet({ hasMeet: !!d?.hasMeet, url: d?.meetUrl ?? null, hasVideo: !!d?.hasVideo, youtubeId: d?.youtubeId ?? null }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [ev, session, authLoading]);

  // Player is shown from 12 h before the start (YouTube shows its own countdown) until the end, so
  // "Watch the lecture" clicked earlier in the day lands on the video, not an empty chat.
  const showPlayer = variant === 'banner' && !!ev && !!meet?.youtubeId && Date.now() >= Date.parse(ev.startsAt) - 12 * 3600_000;
  // Watching counts as joining (analytics + the "Polaznik Uvoda" badge trigger), once per event.
  useEffect(() => {
    if (!showPlayer || !ev || tracked.current === ev.id) return;
    tracked.current = ev.id;
    track('live_event_join', { event_id: ev.id, title: ev.title, via: 'youtube' });
  }, [showPlayer, ev]);

  if (!ev) return null;
  const signupHref = '/sign-up-login-screen?mode=signup&next=%2Flive';
  const onJoin = () => track('live_event_join', { event_id: ev.id, title: ev.title });
  const when = describeEventTime(ev, lang === 'sr' ? 'sr' : 'en');
  const href = liveRoomHref(ev.symbol);
  const inRoom = currentRoom != null && (ev.symbol ?? '') === currentRoom;
  const dot = (
    <span className="relative flex h-2 w-2 shrink-0">
      {when.live && <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: 'var(--bullish)' }} />}
      <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: when.live ? 'var(--bullish)' : 'var(--accent)' }} />
    </span>
  );

  if (variant === 'card') {
    return (
      <div className="rounded-2xl p-4 sm:p-5" style={{ background: 'var(--inverse-bg)', color: '#fff' }}>
        <div className="flex items-center gap-2 text-[11px] font-extrabold uppercase" style={{ letterSpacing: '1.2px', color: 'var(--inverse-muted)' }}>
          {dot} {when.live ? t('Live now') : t('Live session')} · {when.label}
        </div>
        <div className="mt-2 font-heading" style={{ fontSize: 20, fontWeight: 800 }}>{ev.title}</div>
        {ev.description && <p className="mt-1 text-sm" style={{ color: 'var(--inverse-muted)', lineHeight: 1.5 }}>{ev.description}</p>}
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          {meet?.hasVideo && (meet.youtubeId ? (
            <Link href={href} className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold" style={{ background: '#fff', color: 'var(--inverse-bg)' }}>
              <Youtube size={14} /> {t('Watch the lecture')}
            </Link>
          ) : (
            <Link href={signupHref} className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold" style={{ background: '#fff', color: 'var(--inverse-bg)' }}>
              <Youtube size={14} /> {t('Sign up free to watch the lecture')}
            </Link>
          ))}
          {meet?.hasMeet && !meet?.hasVideo && (meet.url ? (
            <a href={meet.url} target="_blank" rel="noopener noreferrer" onClick={onJoin} className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold" style={{ background: '#fff', color: 'var(--inverse-bg)' }}>
              <Video size={14} /> {t('Join the lecture')}
            </a>
          ) : (
            <Link href={signupHref} className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold" style={{ background: '#fff', color: 'var(--inverse-bg)' }}>
              <Video size={14} /> {t('Sign up free for the lecture link')}
            </Link>
          ))}
          {!meet?.hasVideo && <Link href={href} className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold" style={meet?.hasMeet ? { border: '1px solid rgba(255,255,255,0.35)', color: '#fff' } : { background: '#fff', color: 'var(--inverse-bg)' }}>
            <Radio size={14} /> {meet?.hasMeet ? t('Chat room') : when.live ? t('Join now') : t('Open the room')} {ev.symbol ? `· ${ev.symbol}` : ''}
          </Link>}
          <span className="text-xs" style={{ color: 'var(--inverse-muted)' }}>{t('Free with a ShowMeTrade account')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="shrink-0">
    <div className="flex items-center gap-3 px-4 py-2.5 border-b flex-wrap" style={{ background: when.live ? 'var(--bullish-muted)' : 'var(--accent-muted)', borderColor: 'var(--border)' }}>
      {dot}
      <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{ev.title}</span>
      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>· {when.label}{ev.hostUsername ? ` · @${ev.hostUsername}` : ''}</span>
      <span className="ml-auto flex items-center gap-3">
        {meet?.hasVideo && !meet.youtubeId && (
          <Link href={signupHref} className="text-sm font-bold" style={{ color: 'var(--accent)' }}>{t('Sign up free to watch the lecture')} →</Link>
        )}
        {showPlayer && (
          <button onClick={() => setVideoOpen((o) => !o)} className="inline-flex items-center gap-1 text-sm font-bold" style={{ color: 'var(--accent)' }}>
            {videoOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {videoOpen ? t('Hide video') : t('Show video')}
          </button>
        )}
        {meet?.hasMeet && (meet.url ? (
          <a href={meet.url} target="_blank" rel="noopener noreferrer" onClick={onJoin} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1 text-sm font-bold" style={{ background: 'var(--accent)', color: '#fff' }}>
            <Video size={13} /> {t('Join the lecture')}
          </a>
        ) : (
          !meet.hasVideo && <Link href={signupHref} className="text-sm font-bold" style={{ color: 'var(--accent)' }}>{t('Sign up free for the lecture link')} →</Link>
        ))}
        {!inRoom && (
          <Link href={href} className="text-sm font-bold" style={{ color: 'var(--accent)' }}>
            {when.live ? t('Join now') : t('Open the room')} {ev.symbol ?? ''} →
          </Link>
        )}
      </span>
    </div>
    {showPlayer && videoOpen && (
      <div className="border-b px-2 py-2 md:px-4" style={{ borderColor: 'var(--border)', background: '#000' }}>
        <div className="mx-auto" style={{ width: 'min(100%, calc(42dvh * 16 / 9))', aspectRatio: '16 / 9' }}>
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${meet!.youtubeId}?rel=0&modestbranding=1&playsinline=1`}
            title={ev.title}
            className="w-full h-full rounded-lg"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    )}
    </div>
  );
}
